<template>
    <AppFormValidator
        ref="validator"
        #default="{ errors }"
        :model="model"
        :rules="rules"
    >
        <CForm @submit.prevent>
            <CInput
                v-model="model.title"
                horizontal
                label="Заголовок"
                placeholder="Заголовок"
                :is-valid="errors.title && errors.title.isValid"
            />
            <CInput
                v-model.number="model.sort"
                horizontal
                label="Порядок сортировки"
                placeholder="Напр. 100"
                type="number"
                min="0"
            />
            <CRow form class="form-group">
                <CCol tag="label" class="col-form-label" sm="3">Тип опроса</CCol>
                <CCol sm="4">
                    <CSelect
                        :value.sync="model.type"
                        :options="typeOptions"
                        horizontal
                    />
                </CCol>
            </CRow>
            <CTextarea
                v-model="model.description"
                horizontal
                label="Описание"
                placeholder="Описание"
            />

            <CRow form class="form-group">
                <CCol
                    tag="label"
                    class="col-form-label"
                    sm="3"> Дата завершения </CCol>
                <CCol sm="4">
                    <DatePicker
                        v-model="model.due_date"
                        :language="datepicker.lang"
                        :format="datepicker.format"
                        input-class="form-control custom-datepicker"
                        placeholder="Дата завершения" />
                </CCol>
                <CCol sm="5">
                    <CButton
                        @click="model.due_date = null"
                    >Убрать ограничение
                    </CButton>
                </CCol>
            </CRow>

            <CRow form class="form-group">
                <CCol tag="label" class="col-form-label" sm="3"> Промокод для награды </CCol>
                <CCol sm="9">
                    <div class="d-flex" style="gap: 8px; align-items: center;">
                        <input
                            v-model="promoQuery"
                            type="text"
                            class="form-control"
                            placeholder="Поиск промокода по коду..."
                            @input="loadPromocodes"
                            style="max-width: 260px;"
                        />
                        <CSelect
                            class="mb-0"
                            :value.sync="selectedPromocode"
                            :options="formattedPromocodes"
                            horizontal
                        />
                        <CButton variant="outline" color="secondary" @click="clearPromocode">Без награды</CButton>
                    </div>
                </CCol>
            </CRow>

            <CCard class="mb-3">
                <CCardHeader>Аудитория и стили</CCardHeader>
                <CCardBody>
                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Аудитория</CCol>
                        <CCol sm="4">
                            <CSelect
                                :value.sync="model.audience"
                                :options="audienceOptions"
                                horizontal
                            />
                        </CCol>
                    </CRow>
                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Интро фон</CCol>
                        <CCol sm="9">
                            <div class="mb-2" v-if="model.intro_background">
                                <img :src="imageUrl(model.intro_background)" style="max-width:200px;" />
                            </div>
                            <input type="file" @change="onFileChange($event, 'intro_background')" class="form-control-file" />
                        </CCol>
                    </CRow>
                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Цвет кнопки Интро</CCol>
                        <CCol sm="9">
                            <CInput v-model="model.intro_button_background" placeholder="#HEX / gradient" />
                        </CCol>
                    </CRow>

                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Цвет кнопки Продолжить</CCol>
                        <CCol sm="9">
                            <CInput v-model="model.continue_button_background" placeholder="#HEX / gradient" />
                        </CCol>
                    </CRow>
                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Цвет кнопки Спасибо</CCol>
                        <CCol sm="9">
                            <CInput v-model="model.thankyou_button_background" placeholder="#HEX / gradient" />
                        </CCol>
                    </CRow>
                    <CRow form class="form-group">
                        <CCol tag="label" class="col-form-label" sm="3">Финальный фон</CCol>
                        <CCol sm="9">
                            <div class="mb-2" v-if="model.thankyou_background">
                                <img :src="imageUrl(model.thankyou_background)" style="max-width:200px;" />
                            </div>
                            <input type="file" @change="onFileChange($event, 'thankyou_background')" class="form-control-file" />
                        </CCol>
                    </CRow>
                </CCardBody>
            </CCard>

            <CRow form class="form-group">
                <CCol
                    tag="label"
                    class="col-form-label"
                    sm="3"> Включено </CCol>
                <CCol sm="9">
                    <CSwitch
                        class=""
                        color="success"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.enabled"
                    />
                </CCol>
            </CRow>

            <div class="flex mt-4">
                <CButton
                    color="primary"
                    @click="submit"
                >{{ submitButtonTitle }}
                </CButton>
                <CButton
                    v-if="model.id"
                    variant="outline"
                    color="danger"
                    class="ml-4"
                    @click="promptDelete"
                >Удалить
                </CButton>
            </div>
        </CForm>

        <AppConfirmDialog
            ref="confirmDialog"
            title="Подтверждение"
            color="danger"
            text="Вы действительно хотите удалить запись? Это действие нельзя будет отменить."
            @confirm="confirmDelete"
        />
    </AppFormValidator>
</template>

<script>
    import to from 'await-to-js';
    import DatePicker from 'vuejs-datepicker';
    import { ru } from 'vuejs-datepicker/dist/locale';
    import { morphism } from 'morphism';
    import { requiredRule } from '@/utils/rules';
    import AppFormValidator from '@/components/AppFormValidator';
    import AppConfirmDialog from '@/components/AppConfirmDialog';
    const { DELIVERY_TYPE } = require('../../utils/order');

    export default {
        components: {
            AppFormValidator,
            AppConfirmDialog,
            DatePicker,
        },

        props: {
            entity: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                checkReferalProgram: false,
                model: {
                },
                schema: {
                    id: 'id',
                    title: 'title',
                    description: 'description',
                    sort: 'sort',
                    type: 'type',
                    due_date: 'due_date',
                    enabled: 'enabled',
                    audience: 'audience',
                    intro_background: 'intro_background',
                    intro_button_background: 'intro_button_background',
                    thankyou_button_background: 'thankyou_button_background',

                    thankyou_background: 'thankyou_background',
                    continue_button_background: 'continue_button_background',
                },
                selectedPromocode: null,
                promocodes: [],
                promoQuery: '',
                rules: {
                    title: [requiredRule],
                },
                datepicker: {
                    lang: ru,
                    format: 'dd.MM.yyyy',
                    dateFrom: null,
                    dateTo: null,
                },
            };
        },

        computed: {
            submitButtonTitle() {
                return typeof this.entity.id === 'number' ? 'Сохранить' : 'Создать';
            },
            formattedPromocodes() {
                return [
                    { label: 'Без награды', value: null }
                ].concat(this.promocodes.map(item => ({ label: item.code, value: item.id })))
            },
            audienceOptions() {
                return [
                    { label: 'Новые пользователи', value: 'new_users' },
                    { label: 'Все пользователи', value: 'all_users' },
                ];
            },
            typeOptions() {
                return [
                    { label: 'С наградой', value: 'reward' },
                    { label: 'По вопросу в сутки', value: 'one_per_day' },
                ];
            },
        },

        async mounted(){
            await this.loadPromocodes();
            this.setModel();
        },

        methods: {
            disableEntity(event, code) {
                this.disabledEntities[code] = event.target.checked;
            },
            setModel() {
                this.model = morphism(this.schema, this.entity);
                this.selectedPromocode = this.entity.promocode_id;
                if (!this.model.type) this.model.type = 'reward';
                if (typeof this.model.sort !== 'number') this.model.sort = 100;
            },

            async submit() {
                const [error] = await to(this.$refs.validator.validate());

                if (error) {
                    return false;
                }

                const { id, ...payload } = this.model;
                payload.promocode_id = this.selectedPromocode;
                if (!payload.audience) payload.audience = 'new_users';
                if (!payload.type) payload.type = 'reward';

                if (typeof id === 'number') {
                    const [err] = await to(this.axios.put(`/surveys/${id}`, payload));
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось сохранить запись.');
                    }
                } else {
                    const [err] = await to(this.axios.post('/surveys', payload));
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось создать новую запись.');
                    }
                }

                this.$router.push(`/surveys`);
            },

            async onFileChange(e, field) {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                    const { data } = await this.axios.post('/files/upload', formData);
                    this.$set(this.model, field, data.filename);
                } catch (e) {
                    this.$root.$emit('showError', 'Ошибка загрузки файла');
                }
            },
            imageUrl(filename) {
                return process.env.VUE_APP_STATIC_BASE_URL + '/' + filename;
            },
            clearPromocode() {
                this.selectedPromocode = null;
            },
            async loadPromocodes() {
                try {
                    const params = this.promoQuery ? `?q=${encodeURIComponent(this.promoQuery)}` : '';
                    const { data } = await this.axios.get(`/surveys/promocodes${params}`);
                    this.promocodes = data;
                } catch (e) {}
            },

            promptDelete() {
                this.$refs.confirmDialog.open();
            },

            async confirmDelete() {
                if (!this.model.id) {
                    return false;
                }

                const [error] = await to(
                    this.axios.delete(`/surveys/${this.model.id}`)
                );

                if (error) {
                    return this.$root.$emit('showError', 'Ошибка удаления записи.');
                }

                this.$emit('deleted');
            },
        },
    };
</script>
<style>
input.custom-datepicker {
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    border: 1px solid;
    border-color: #d8dbe0;
    width: 100%;
}
</style>
